import React from 'react';
import { toast } from 'react-hot-toast';
import useQueueStore from '../../../stores/queueStore';
import usePlayerStore from '../../../stores/playerStore';
import { fetchPartyStarters } from '../../../services/partyStarters';

/**
 * Host-only "Start with one of these" after an initial vibe save.
 * Omitted entirely when there are zero trusted playable suggestions.
 * Parent must only mount this for the host.
 */
export default function PartyStarterSuggestions({
  eventId,
  enabled,
  vibeSaved,
  fetchStarters = fetchPartyStarters,
}) {
  const addToQueue = useQueueStore((state) => state.addToQueue);
  const queue = useQueueStore((state) => state.queue);
  const currentSong = usePlayerStore((state) => state.currentSong);
  const setCurrentSong = usePlayerStore((state) => state.setCurrentSong);
  const [tracks, setTracks] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const requestedForSaveRef = React.useRef(null);

  const playbackStarted = Boolean(currentSong) || (Array.isArray(queue) && queue.length > 0);

  React.useEffect(() => {
    setDismissed(false);
    setTracks([]);
    requestedForSaveRef.current = null;
  }, [eventId]);

  React.useEffect(() => {
    if (!enabled || !eventId || !vibeSaved || dismissed || playbackStarted) {
      return undefined;
    }
    const saveKey = `${vibeSaved.feelId}|${(vibeSaved.styleIds || []).join(',')}|${vibeSaved.updatedAt || vibeSaved.submittedAt || ''}`;
    if (requestedForSaveRef.current === saveKey) return undefined;

    let cancelled = false;
    requestedForSaveRef.current = saveKey;
    setLoading(true);
    fetchStarters(eventId).then((result) => {
      if (cancelled) return;
      setTracks(Array.isArray(result?.tracks) ? result.tracks.slice(0, 3) : []);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setTracks([]);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    eventId,
    vibeSaved,
    dismissed,
    playbackStarted,
    fetchStarters,
  ]);

  if (!enabled || !vibeSaved || dismissed || playbackStarted) return null;
  if (!loading && tracks.length === 0) return null;

  const playStarter = async (track) => {
    const added = await addToQueue(track);
    if (added === false) {
      toast.error('Could not add that song');
      return;
    }
    if (!currentSong) {
      setCurrentSong(track);
    }
    setDismissed(true);
  };

  return (
    <section
      aria-label="Start with one of these"
      className="mt-4 w-full min-w-0"
      data-testid="party-starter-suggestions"
    >
      <h3 className="text-base font-semibold text-white">Start with one of these</h3>
      <p className="mt-1 text-xs text-white/60">Based on your starting Vibe</p>
      {loading ? (
        <p className="mt-3 text-sm text-white/50">Finding starter songs…</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {tracks.map((track) => (
            <li
              key={track.recordingId}
              className="flex items-center gap-3 rounded-lg bg-white/5 p-2"
            >
              {track.thumbnail ? (
                <img src={track.thumbnail} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
              ) : (
                <div className="h-10 w-10 shrink-0 rounded bg-white/10" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-white">{track.title}</p>
                {track.artist ? (
                  <p className="truncate text-xs text-white/50">{track.artist}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => playStarter(track)}
                className="shrink-0 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 transition-colors"
              >
                Play
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
